import React from 'react';

import Chip from '~/components/core/Atomic/Chip/Chip';
import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import { KIND_FILTERS } from '../constants';
import type { KindFilter } from '../types';

interface KindChipsProps {
  value: KindFilter;
  onChange: (kind: KindFilter) => void;
}

/**
 * Level one of the panel's filter: what kind of thing.
 *
 * Each chip wears its kind's own icon and colour, so the control looks like the cards it filters
 * rather than a second grey toggle row under the status filter.
 *
 * **No counts on the chips.** A count is either on all four or on none — one on "All" alone is the
 * arithmetic done and then withheld from the three options that would act on it.
 */
const KindChips: React.FC<KindChipsProps> = ({ value, onChange }) => (
  <div role="group" aria-label="Kind" className="flex flex-wrap items-center gap-8">
    {KIND_FILTERS.map(({ value: kind, label, appearance }) => {
      const isActive = kind === value;

      return (
        <Chip
          key={kind}
          onClick={() => onChange(kind)}
          aria-pressed={isActive}
          variant="outlined"
          icon={
            appearance ? (
              <span className={cn('grid h-24 w-24 shrink-0 place-items-center rounded-lg', appearance.chip)}>
                <appearance.Icon size={14} iconColor={appearance.iconColor} />
              </span>
            ) : undefined
          }
          label={
            <Text variant={Text.VARIANTS.XS} weight={isActive ? Text.WEIGHTS.SEMI_BOLD : Text.WEIGHTS.REGULAR}>
              {label}
            </Text>
          }
          className={cn('m-0 h-auto py-4', isActive ? 'border-slate-800 bg-slate-100' : 'border-slate-300 bg-white')}
        />
      );
    })}
  </div>
);

export default KindChips;
